import { projectPointOntoLine } from '@ue-too/curve';
import { PointCal } from '@ue-too/math';

import { sitsOnComputedPoint } from './attach-anchor';
import { arcContains, isConcentricGuide, type ArcRing, type Guide, type GuideId } from './guide-types';
import { isExactlyResolvedGuideSnap } from './tbc-types';
import type { GuideSnap, TbcPoint } from './tbc-types';
import { pointAtS, projectPointToCurve } from './bezier-geometry';

export type SnapResult = {
    point: TbcPoint;
    binding: GuideSnap;
};

/** Snap a world point to the closest point on any guide within `thresholdWorld`.
 *  Returns the projected point + the binding (guide id + local coord) so the
 *  caller can store the attachment, not just the snapped position.
 *  `exclude` rejects candidate bindings before they compete — per ring on
 *  concentric guides, so the guide's other rings can still win. */
export function snapPointToGuides(
    world: TbcPoint,
    guides: ReadonlyMap<GuideId, Guide>,
    thresholdWorld: number,
    exclude?: (binding: GuideSnap) => boolean,
): SnapResult | null {
    let best: { d: number; result: SnapResult } | null = null;
    for (const g of guides.values()) {
        const candidate = projectOnto(world, g, exclude);
        if (candidate == null) continue;
        const d = PointCal.distanceBetweenPoints(world, candidate.point);
        if (d > thresholdWorld) continue;
        if (best == null || d < best.d) best = { d, result: candidate };
    }
    return best?.result ?? null;
}

/** Recompute the world position of a handle from its guide binding, after the
 *  guide moves or any time the handle needs to be re-pinned to its guide.
 *  TopAttachSnap bindings are resolved by the engine (they ride a TBC top, not
 *  a guide) — they aren't handled here. */
export function resolveGuideSnapBinding(
    binding: GuideSnap,
    guides: ReadonlyMap<GuideId, Guide>,
): TbcPoint | null {
    const g = guides.get(binding.guideId);
    if (g == null) return null;
    switch (binding.kind) {
        case 'guide-line':
            if (g.type !== 'line') return null;
            return PointCal.linearInterpolation(g.start, g.end, binding.t) as TbcPoint;
        case 'guide-circle': {
            if (!isConcentricGuide(g)) return null;
            const ring = g.rings.find(r => r.id === binding.ringId);
            if (ring == null) return null;
            return {
                x: g.center.x + ring.radius * Math.cos(binding.angle),
                y: g.center.y + ring.radius * Math.sin(binding.angle),
            };
        }
        case 'guide-bezier': {
            if (g.type !== 'bezier-curve') return null;
            return pointAtS(g, binding.s);
        }
    }
    // Exhaustiveness backstop: a new GuideSnap kind fails to compile here
    // instead of silently returning undefined (noImplicitReturns is off).
    const _exhaustive: never = binding;
    void _exhaustive;
    return null;
}

/** Whether a handle at `at` already rides `resolved`, the point its guide
 *  snap resolves to. A line guide's point is IEEE arithmetic only, the same
 *  on every engine, so it must match exactly. A ring's (cos/sin) or a curve's
 *  (arc-length table) can round differently in the last bit on another
 *  engine, so a handle within ε of it is on it (sitsOnComputedPoint).
 *  Resolution is absolute, never incremental, so after any re-ride the
 *  handle is within ε of the guide's current point — exactly on it for a
 *  line — and the tolerance never accumulates. */
export function ridesGuidePoint(snap: GuideSnap, at: TbcPoint, resolved: TbcPoint): boolean {
    if (at.x === resolved.x && at.y === resolved.y) return true;
    if (isExactlyResolvedGuideSnap(snap)) return false;
    return sitsOnComputedPoint(at, resolved);
}

/** The foot of the perpendicular from `point` to the segment `start`–`end`,
 *  with its fraction along it, or null when the foot lies outside the
 *  segment (no end-cap snapping).
 *
 *  `projectPointOntoLine` without its side effects: `@ue-too/math` writes
 *  `z = 0` into a 2-D point it combines with a 3-D one, and the projection's
 *  unit vector is 3-D. Handed a stored line's own endpoints, it gave them a
 *  `z` in place — an edit no history transaction or Yjs capture sees — and
 *  the projected point carried one. So it works on copies, and the point it
 *  returns is 2-D. */
function projectOntoSegment(point: TbcPoint, start: TbcPoint, end: TbcPoint): { point: TbcPoint; offset: number } | null {
    const res = projectPointOntoLine({ x: point.x, y: point.y }, { x: start.x, y: start.y }, { x: end.x, y: end.y });
    if (!res.within || res.projectionPoint == null || res.offset == null) return null;
    return { point: { x: res.projectionPoint.x, y: res.projectionPoint.y }, offset: res.offset };
}

/** The fraction along `start`–`end` of `point`'s perpendicular foot, which
 *  may lie outside [0, 1], or null when `projectPointOntoLine` gives none.
 *  Worked on copies, as {@link projectOntoSegment} is: the points passed in
 *  are often stored ones (a line guide's endpoints, a stitch's handle). */
export function lineOffsetOf(point: TbcPoint, start: TbcPoint, end: TbcPoint): number | null {
    return projectPointOntoLine({ x: point.x, y: point.y }, { x: start.x, y: start.y }, { x: end.x, y: end.y }).offset ?? null;
}

/** The fraction along `a`–`b` of `p`'s perpendicular foot, clamped to [0, 1]
 *  (0 for a zero-length segment). Unlike {@link lineOffsetOf} it never returns
 *  null and never leaves the segment. */
export function clampedSegmentParam(p: TbcPoint, a: TbcPoint, b: TbcPoint): number {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const len2 = abx * abx + aby * aby;
    if (len2 === 0) return 0;
    const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
    return Math.max(0, Math.min(1, t));
}

function projectOnto(point: TbcPoint, guide: Guide, exclude?: (binding: GuideSnap) => boolean): SnapResult | null {
    switch (guide.type) {
        case 'line': {
            const res = projectOntoSegment(point, guide.start, guide.end);
            if (res == null) return null;
            const binding: GuideSnap = { guideId: guide.id, kind: 'guide-line', t: res.offset };
            if (exclude?.(binding)) return null;
            return { point: res.point, binding };
        }
        case 'bezier-curve': {
            const res = projectPointToCurve(guide, point);
            if (res == null) return null;
            const binding: GuideSnap = { guideId: guide.id, kind: 'guide-bezier', s: res.s };
            if (exclude?.(binding)) return null;
            return { point: res.point, binding };
        }
        case 'concentric-circles':
        case 'concentric-arcs': {
            if (guide.rings.length === 0) return null;
            const dx = point.x - guide.center.x;
            const dy = point.y - guide.center.y;
            const r = Math.hypot(dx, dy);
            // Right at the center: ambiguous angle, skip rather than snap to
            // an arbitrary point on each ring.
            if (r === 0) return null;
            const angle = Math.atan2(dy, dx);
            let bestD = Infinity;
            let best: SnapResult | null = null;
            const isArcs = guide.type === 'concentric-arcs';
            for (const ring of guide.rings) {
                if (isArcs) {
                    const arc = ring as ArcRing;
                    if (!arcContains(arc.startAngle, arc.endAngle, angle)) continue;
                }
                const d = Math.abs(r - ring.radius);
                if (d >= bestD) continue;
                const binding: GuideSnap = { guideId: guide.id, kind: 'guide-circle', ringId: ring.id, angle };
                if (exclude?.(binding)) continue;
                bestD = d;
                best = {
                    point: {
                        x: guide.center.x + ring.radius * Math.cos(angle),
                        y: guide.center.y + ring.radius * Math.sin(angle),
                    },
                    binding,
                };
            }
            return best;
        }
    }
}

/** Stable identity key for one bezier control point, shared by the snapper and
 *  the engine's exclusion sets so both agree on what "the same point" means. */
export function controlPointKey(guideId: GuideId, anchorId: string, part: 'point' | 'left' | 'right'): string {
    return `${guideId}:${anchorId}:${part}`;
}

/** Snap a world point to the nearest bezier control point (anchor point or
 *  either handle) on ANY bezier guide within `thresholdWorld`, skipping any
 *  point whose {@link controlPointKey} is in `exclude` (the points that move
 *  with the active drag). Returns a fresh copy or null. Same-curve points are
 *  valid targets unless excluded. */
export function snapPointToControlPoints(
    world: TbcPoint,
    guides: ReadonlyMap<GuideId, Guide>,
    thresholdWorld: number,
    exclude: ReadonlySet<string>,
): TbcPoint | null {
    let best: { d: number; point: TbcPoint } | null = null;
    for (const g of guides.values()) {
        if (g.type !== 'bezier-curve') continue;
        for (const a of g.anchors) {
            const candidates: Array<['point' | 'left' | 'right', TbcPoint]> = [
                ['point', a.point],
                ['left', a.leftHandle],
                ['right', a.rightHandle],
            ];
            for (const [part, p] of candidates) {
                if (exclude.has(controlPointKey(g.id, a.id, part))) continue;
                const d = PointCal.distanceBetweenPoints(world, p);
                if (d > thresholdWorld) continue;
                if (best == null || d < best.d) best = { d, point: { x: p.x, y: p.y } };
            }
        }
    }
    return best?.point ?? null;
}
